/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { StreakPostPrayer, SummaryPostPrayer } from "@packages/sdk";
import { weekStartsOn } from "@packages/sdk";
import { addDays } from "date-fns/addDays";
import { endOfWeek } from "date-fns/endOfWeek";
import { format } from "date-fns/format";
import { isSameDay } from "date-fns/isSameDay";
import { startOfWeek } from "date-fns/startOfWeek";
import { useLocale } from "react-aria";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  colors,
  lineHeights,
  numericPercentages,
  radius,
  spacing,
} from "../../../../../../global/emotion";
import { Text } from "../../../../../components/_base/_core/Text/Text.emotion";

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  maxWidth: spacing.maxWidthWithPaddingXS,
  rowGap: spacing.s,
  width: numericPercentages[100],
};
const weekStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.ms,
  justifyContent: "center",
  margin: spacing.m,
  width: numericPercentages[100],
};
const dayStyle: Interpolation<ThemeType> = (theme) => ({
  background: colors.transparent,
  borderRadius: radius.full,
  boxSizing: "content-box",
  color: theme.semanticColors.primary,
  display: "flex",
  height: lineHeights.bodyL,
  padding: spacing.xxs,
  placeContent: "center",
  placeItems: "center",
  width: lineHeights.bodyL,
});
const activeStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.primary,
  color: theme.semanticColors.background,
});

export type ActivitySummaryProps = {
  postPrayer: SummaryPostPrayer | StreakPostPrayer;
};

export const ActivitySummary = ({ postPrayer }: ActivitySummaryProps) => {
  const { locale } = useLocale();
  const firstDayOfWeek = weekStartsOn(locale);
  const today = new Date();
  const firstDateOfWeek = startOfWeek(today, {
    weekStartsOn:
      firstDayOfWeek === "sat" ? 6 : firstDayOfWeek === "sun" ? 0 : 1,
  });
  const lastDateOfWeek = endOfWeek(today, {
    weekStartsOn:
      firstDayOfWeek === "sat" ? 6 : firstDayOfWeek === "sun" ? 0 : 1,
  });
  const t = useTranslations();

  const days = [];
  let movingDay = firstDateOfWeek;
  while (movingDay <= lastDateOfWeek) {
    days.push(
      <Text
        key={movingDay.toISOString()}
        as={"div"}
        type={"body"}
        size={"m"}
        css={[
          dayStyle,
          postPrayer.streak.activity.some(
            (day) =>
              isSameDay(movingDay, new Date(day.date)) && day.has_session,
          )
            ? activeStyle
            : null,
        ]}
      >
        {format(movingDay, "EEEEE")}
      </Text>,
    );
    movingDay = addDays(movingDay, 1);
  }

  return (
    <div css={containerStyle}>
      <Text key={"header"} as={"p"} type={"title"} size={"xl"}>
        {(postPrayer as StreakPostPrayer).title ??
          t("general_word_congrats_exclamation")}
      </Text>
      <Text key={"description"} as={"p"} type={"body"} size={"m"}>
        {(postPrayer as StreakPostPrayer).subtitle ??
          t("post_prayer_streak_plural", {
            post_prayer_streak_plural: postPrayer.streak.current_total,
          })}
      </Text>
      <div css={weekStyle} key={"week"}>
        {days}
      </div>
    </div>
  );
};
